import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Lock, User, Users } from 'lucide-react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { ReferentialKey } from 'api/referential';
import { GenericLoading } from 'components/ui/Loader';
import { BaseSection } from 'components/layout/Section/BaseSection';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { InputText } from 'components/forms/InputText';
import { PaymentModeOption } from 'components/forms/PaymentModeOption';
import { ValidationTracker } from 'components/domain/ValidationTracker/ValidationTracker';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { BankAccountRibSelector } from 'features/subscription-process/contractualization-process/components/bank-account';
import { generateMonthlyDropdown } from 'helpers/generateMonthlyDropdown';
import { contractsAPI } from 'services/swagger-api';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';

import { toDayInput } from 'core/utils/dates';
export interface SpecialConditionsFormHandle {
  /** Sauvegarde les CP + le mandat. Throw si erreur. */
  save: () => Promise<void>;
}

interface SpecialConditionsFormProps {
  groupContractId: string;
  /** Id du souscripteur — requis pour afficher le sélecteur de RIB. */
  subscriberId?: string | null;
  /** True si contrat cadre (affiche le toggle "Qui paie ?"). */
  isCadre: boolean;
  /** Notifie le parent quand la validité du form change. */
  onValidityChange?: (isValid: boolean) => void;
  /** Notifie le parent quand le contrat passe en verrouillé (chargé depuis le back). */
  onLockedChange?: (isLocked: boolean) => void;
}

interface FormValues {
  effectiveDate: string;        // YYYY-MM-DD
  mainDueDateMonth: number | '';// 1-12 — le jour est implicitement 01
  paymentByAliments: boolean;   // CADRE only — chaque aliment paie son propre prélèvement
  bankAccountId: string;        // ignoré si paymentByAliments
  // Périodicité : forcée à Annuelle côté back pour tout contrat collectif — affichée
  // en lecture seule, pas d'input utilisateur.
  // Préavis : valeur fixe côté back via config — pas d'input utilisateur.
}

/** Seule la périodicité annuelle est disponible pour un contrat collectif. */
const ANNUAL_PERIODICITY_CODE = 'AN';

const EMPTY_VALUES: FormValues = {
  effectiveDate: '',
  mainDueDateMonth: '',
  paymentByAliments: false,
  bankAccountId: '',
};

const buildValidationSchema = () =>
  Yup.object({
    effectiveDate: Yup.string().required("La date d'effet est requise"),
    mainDueDateMonth: Yup.number()
      .typeError("Le mois est requis")
      .min(1, 'Le mois doit être entre 1 et 12')
      .max(12, 'Le mois doit être entre 1 et 12')
      .required("Le mois de l'échéance est requis"),
    paymentByAliments: Yup.boolean(),
    // bankAccountId : requis SAUF si paymentByAliments = true
    bankAccountId: Yup.string().when('paymentByAliments', {
      is: false,
      then: (s) => s.required('Le compte bancaire est requis pour générer le mandat SEPA'),
      otherwise: (s) => s.notRequired(),
    }),
  });

export const SpecialConditionsForm = forwardRef<SpecialConditionsFormHandle, SpecialConditionsFormProps>(
  ({ groupContractId, subscriberId, isCadre, onValidityChange, onLockedChange }, ref) => {
    const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
    const dispatch = useAppDispatch();
    const periodicities = useAppSelector(selectReferentialDataByKey(ReferentialKey.ContractPeriodicity)) || [];
    const formikRef = useRef<FormikProps<FormValues>>(null);

    const [initialValues, setInitialValues] = useState<FormValues>(EMPTY_VALUES);
    const [loaded, setLoaded] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    useEffect(() => {
      dispatch(getMultipleReferentialsByKeys([ReferentialKey.ContractPeriodicity]));
    }, [dispatch]);

    // Charge le contrat + l'état mandats (pour récupérer paymentByAliments, bankAccountId, isLocked)
    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const [contract, mandats] = await Promise.all([
            contractsAPI.getGroupContractByID({ groupContractId }),
            contractsAPI.getGroupContractMandats({ groupContractId }),
          ]);
          if (cancelled) return;
          setIsLocked(mandats.isLocked);
          onLockedChange?.(mandats.isLocked);
          // Préremplissage des dates quand la CP est vierge : date d'effet =
          // aujourd'hui, échéance principale = mois anniversaire de la date d'effet
          // (jour fixé à 01). L'utilisateur reste libre de modifier avant de sauver.
          const effectiveDate = contract.effectiveDate
            ? toDayInput(contract.effectiveDate)
            : toDayInput(new Date());
          setInitialValues({
            effectiveDate,
            mainDueDateMonth:
              contract.mainDueDateMonth ?? Number(effectiveDate.slice(5, 7)),
            paymentByAliments: mandats.paymentByAliments,
            bankAccountId: mandats.selectedBankAccountId ?? '',
          });
        } catch (err) {
          console.error('Failed to load special conditions context', err);
        } finally {
          if (!cancelled) setLoaded(true);
        }
      })();
      return () => { cancelled = true; };
    }, [groupContractId, onLockedChange]);

    useEffect(() => {
      onValidityChange?.(isFormValid && !isLocked);
    }, [isFormValid, isLocked, onValidityChange]);

    const annualPeriodicityLabel = useMemo(
      () =>
        periodicities.find((p: any) => p.code === ANNUAL_PERIODICITY_CODE)?.label ??
        'Annuelle',
      [periodicities],
    );

    // Items "01 janvier" → "01 décembre" (key = numéro du mois 1-12). Le jour est fixé à 01.
    const monthItems = useMemo(() => generateMonthlyDropdown(), []);

    const handleSubmit = useCallback(async (values: FormValues) => {
      // 1. Sauvegarde des champs CP "purs" — le jour est fixé à 01 côté front (validé côté back).
      //    La durée de préavis n'est plus envoyée : valeur fixe lue depuis la config back.
      await contractsAPI.updateGroupContract({
        groupContractId,
        updateGroupContractCommand: {
          // La périodicité est forcée à Annuelle par le back — non transmise ici.
          effectiveDate: new Date(values.effectiveDate),
          mainDueDateDay: 1,
          mainDueDateMonth: Number(values.mainDueDateMonth),
        },
      });

      // 2. Sauvegarde du compte bancaire / modalité de paiement → résout/crée les mandats
      await contractsAPI.updateGroupContractMandats({
        groupContractId,
        updateGroupContractMandatsCommand: {
          paymentByAliments: values.paymentByAliments,
          bankAccountId: values.paymentByAliments ? undefined : values.bankAccountId,
        },
      });
    }, [groupContractId]);

    useImperativeHandle(ref, () => ({
      async save() {
        const formik = formikRef.current;
        if (!formik) return;
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
          throw new Error('Validation failed');
        }
        await handleSubmit(formik.values);
      },
    }), [handleSubmit]);

    if (!loaded) {
      return <GenericLoading loading isParentSized />;
    }

    return (
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validationSchema={buildValidationSchema()}
        onSubmit={handleSubmit}
      >
        {({ isValid }) => (
          <>
            <ValidationTracker isValid={isValid} onValidationChange={setIsFormValid} />
            <Form>
              <fieldset disabled={isLocked} className="flex flex-col gap-4">
                {isLocked && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Lock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">
                        {t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.LOCKED_TITLE')}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        {t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.LOCKED_DESCRIPTION')}
                      </p>
                    </div>
                  </div>
                )}

                <SchedulingSection
                  annualPeriodicityLabel={annualPeriodicityLabel}
                  monthItems={monthItems}
                  t={t}
                />

                {isCadre && <PaymentModeSection t={t} />}

                <BankAccountSection
                  subscriberId={subscriberId}
                  isCadre={isCadre}
                  t={t}
                />
              </fieldset>
            </Form>
          </>
        )}
      </Formik>
    );
  },
);

SpecialConditionsForm.displayName = 'SpecialConditionsForm';

// ─────────────────────────────────────────────────────────────────
// Sous-composants (séparés pour utiliser useFormikContext proprement)
// ─────────────────────────────────────────────────────────────────

const SchedulingSection = ({
  annualPeriodicityLabel,
  monthItems,
  t,
}: {
  annualPeriodicityLabel: string;
  monthItems: { key: number; label: string }[];
  t: (k: string) => string;
}) => (
  <BaseSection title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.SCHEDULING')}>
    <div className="grid grid-cols-2 gap-4">
      <FormikInputText
        name="effectiveDate"
        type="date"
        label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.EFFECTIVE_DATE')}
        required
      />
      <div>
        <InputText
          label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PERIODICITY')}
          value={annualPeriodicityLabel}
          disabled
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PERIODICITY_ANNUAL_ONLY')}
        </p>
      </div>
      <FormikDropdown
        name="mainDueDateMonth"
        label={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.MAIN_DUE_DATE')}
        placeholder={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.MONTH_PLACEHOLDER')}
        items={monthItems}
        required
      />
    </div>
  </BaseSection>
);

const PaymentModeSection = ({ t }: { t: (k: string) => string }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  return (
    <BaseSection title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.PAYMENT_MODE')}>
      <div className="flex flex-col gap-3">
        <PaymentModeOption
          icon={<User className="w-6 h-6" />}
          title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PAYMENT_MODE.BY_SUBSCRIBER')}
          description={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PAYMENT_MODE.BY_SUBSCRIBER_HINT')}
          selected={!values.paymentByAliments}
          onClick={() => setFieldValue('paymentByAliments', false)}
        />
        <PaymentModeOption
          icon={<Users className="w-6 h-6" />}
          title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PAYMENT_MODE.BY_ALIMENTS')}
          description={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.PAYMENT_MODE.BY_ALIMENTS_HINT')}
          selected={values.paymentByAliments}
          onClick={() => setFieldValue('paymentByAliments', true)}
        />
      </div>
    </BaseSection>
  );
};

const BankAccountSection = ({
  subscriberId,
  isCadre,
  t,
}: {
  subscriberId?: string | null;
  isCadre: boolean;
  t: (k: string) => string;
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  // En mode cadre + paiement par aliments → section masquée (pas de mandat au niveau cadre).
  if (isCadre && values.paymentByAliments) return null;

  return (
    <BaseSection title={t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.SECTIONS.BANK_ACCOUNT')}>
      {subscriberId ? (
        <BankAccountRibSelector
          personId={subscriberId}
          selectedBankAccountId={values.bankAccountId || undefined}
          onSelect={(id) => setFieldValue('bankAccountId', id)}
        />
      ) : (
        <p className="text-sm text-gray-500 italic">
          {t('GROUP_CONTRACT_PROCESS.SPECIAL_CONDITIONS.BANK_ACCOUNT_REQUIRES_SUBSCRIBER')}
        </p>
      )}
    </BaseSection>
  );
};
